import React from 'react'

interface TablePaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  rowsPerPage?: number
  onRowsPerPageChange?: (rows: number) => void
  rowsPerPageOptions?: number[]
  totalRecords?: number
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 15, 20],
  totalRecords,
}) => {
  const getVisiblePages = () => {
    const pages: (number | string)[] = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      const startPage = Math.max(2, currentPage - 1)
      const endPage = Math.min(totalPages - 1, currentPage + 1)

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      pages.push(totalPages)
    }

    return pages
  }

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page)
    }
  }

  const startRow = totalRecords && rowsPerPage ? (currentPage - 1) * rowsPerPage + 1 : 0
  const endRow = totalRecords && rowsPerPage ? Math.min(currentPage * rowsPerPage, totalRecords) : 0

  return (
    <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 px-4 py-3'>
      {/* Izquierda - Info de registros */}
      <div className='text-muted small'>
        {totalRecords != null && rowsPerPage != null && totalRecords > 0 ? (
          <>
            Mostrando {startRow} - {endRow} de {totalRecords} registros
          </>
        ) : (
          'Sin resultados'
        )}
      </div>

      {/* Centro - Selector de filas por página */}
      {onRowsPerPageChange && (
        <div className='d-flex align-items-center'>
          <label className='me-2 fw-semibold text-muted small mb-0'>Filas por página:</label>
          <select
            className='form-select form-select-sm w-auto'
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          >
            {rowsPerPageOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Derecha - Paginación */}
      <nav>
        <ul className='pagination pagination-sm mb-0'>
          {/* Primera página */}
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => onPageChange(1)} aria-label='Primera'>
              ⏮
            </button>
          </li>

          {/* Anterior */}
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className='page-link'
              onClick={() => onPageChange(currentPage - 1)}
              aria-label='Anterior'
            >
              ◀
            </button>
          </li>

          {/* Páginas visibles */}
          {getVisiblePages().map((page, idx) => (
            <li
              key={idx}
              className={`page-item ${page === currentPage ? 'active' : ''} ${
                page === '...' ? 'disabled' : ''
              }`}
            >
              <button
                className='page-link'
                onClick={() => handlePageClick(page)}
                disabled={page === '...'}
              >
                {page}
              </button>
            </li>
          ))}

          {/* Siguiente */}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className='page-link'
              onClick={() => onPageChange(currentPage + 1)}
              aria-label='Siguiente'
            >
              ▶
            </button>
          </li>

          {/* Última página */}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className='page-link'
              onClick={() => onPageChange(totalPages)}
              aria-label='Última'
            >
              ⏭
            </button>
          </li>
        </ul>
      </nav>
    </div>
  )
}
